import { useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import qs from 'query-string';
import { connect, useDispatch, useStore } from 'react-redux';
import { compose } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { acceptTos, accountSingleStepTransferCreate } from 'src/app/client/models/account';
import Button from 'src/app/components/Elements/Button';
import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import Spinner from 'src/app/components/Elements/Spinner';
import GenericError from 'src/app/components/Error/Generic';
import TextInput from 'src/app/components/Form/TextInput';
import DateTextInput from 'src/app/components/Form/TextInput/Date';
import { PasswordInput } from 'src/app/components/Form/TextInput/Password';
import ToggleInput from 'src/app/components/Form/ToggleInput';
import { Step } from 'src/app/components/Pages/GetStarted/StepLayout/Step';
import {
  filterMedicationFormValues,
  getTransferableMeds,
} from 'src/app/components/PageSections/DoctorTransferForm/utils';
import { useUserCheckPassword } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { termsAndConditions, welcome } from 'src/app/router/paths';
import {
  segmentAnalyticsIdentify,
  onboardingAccountValidate,
  pushWithQueryParams,
  segmentTrack,
  setOnboardingFormValues,
} from 'src/app/state/actions';
import type {
  CreateAccountWithDoctorTransferAndAllMeds,
  OnboardingTransfer,
} from 'src/app/state/actions/onBoardingActions';
import {
  setMedicationSuccess,
  setOnboardingTransfersSuccess,
  transformOnboardingTransfers,
  setOnboardingEscripts,
  getPatientMedications,
  onboardingAccountNotifyTransfer,
} from 'src/app/state/actions/onBoardingActions';
import { getCurrentPathname, getOnboardingFlow } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { Medication } from 'src/app/types';

import { FLOW, MATCH_TYPE, SIGNUP_SOURCES } from '../../consts';
import { ButtonContainer, LegalMessage, StyledSpinnerContainer } from '../../styles';
import styles from '../../styles.module.scss';
import { clearOnboardingStorage, formatPhoneNumber } from '../../util';

import { validation } from './validation';

const toggleValues = [
  { value: 'male', label: 'male' },
  { value: 'female', label: 'female' },
];

const inputNames = Object.keys(validation.initialValues);

const ProfileStepRenderer = ({
  dispatchSegmentTrack,
  pathname,
  onSetOnboardingFormValues,
  flow,
  onboardingForm,
  dispatchAccountOnboardingValidate,
  redirectToConfirmationPage,
}) => {
  const phoneNumberMask = '999-999-9999';

  const { mutateAsync: checkPassword } = useUserCheckPassword();

  const [hasDispatchedEvent, setHasDispatchedEvent] = useState(false);

  useEffect(() => {
    if (!hasDispatchedEvent) {
      dispatchSegmentTrack(UNIFIED.viewOnboardingAboutYou, { flow });
      setHasDispatchedEvent(true);
    }
  }, [dispatchSegmentTrack, hasDispatchedEvent, flow]);

  const formErrorRef = useRef(null);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (formError && formErrorRef?.current) {
      formErrorRef.current.scrollIntoView();
    }
  }, [formError, formErrorRef]);

  const handleGenderSelect = gender => {
    dispatchSegmentTrack(UNIFIED.selectSex, { pagepath: pathname, sex: gender });
    onSetOnboardingFormValues({ sex: gender });
  };

  const { login } = useAuth();
  const store = useStore();
  const state = store.getState();
  const dispatch = useDispatch();

  return (
    <Formik
      validationSchema={validation.schema}
      initialValues={validation.initialValues}
      validateOnChange={true}
      validateOnBlur={true}
      validateOnMount={true}
      onSubmit={async (values, { setErrors, setFieldError }) => {
        const password = values.password;
        const accountId = onboardingForm.leadAccountId;

        try {
          const passwordCheckResp = await checkPassword(password);
          if (!passwordCheckResp.isSafe) {
            setFieldError(
              'password',
              'This password is not secure or is too common. Try increasing length or adding numbers and symbols.'
            );
            return;
          }
        } catch (error) {
          console.log(`Error checking password for accountId=${accountId}`);
        }

        const email = onboardingForm.email;
        const userValues = { email, password };
        const phone = values.phone;
        const isNoRxTransferFlow = onboardingForm.isNoRxTransferFlow;
        const referral = qs.parse(window.location.search)?.cp_referral as string | undefined;

        const medsWithNames = filterMedicationFormValues(onboardingForm.medications) as Required<Medication>[];

        const normalizedMeds: OnboardingTransfer[] = getTransferableMeds(medsWithNames).map(({ attributes }) => {
          const medicationName = `${attributes.primaryName!}`;

          return onboardingForm.isDoctorTransfer
            ? {
                medicationName,
                doctorName: onboardingForm.doctorName,
                doctorPhone: onboardingForm.doctorPhone,
                ...attributes,
              }
            : {
                medicationName,
                ...attributes,
              };
        });

        dispatchSegmentTrack(UNIFIED.submitOnboarding, { pagepath: pathname, contains_control: false, flow });

        // For Segment
        const medication_names = normalizedMeds.map(({ medicationName }) => medicationName);

        const isTransfer = normalizedMeds.length > 0;
        if (isTransfer) {
          dispatchSegmentTrack(UNIFIED.submitTransfer, { page: pathname, medication_names });
          dispatchSegmentTrack(UNIFIED.submitOnboardingTransfer);
          dispatchSegmentTrack(UNIFIED.submitFirstTransfer);
        }

        const signupSource = mapFlowToSignupSource(flow, isNoRxTransferFlow, normalizedMeds);

        const data: CreateAccountWithDoctorTransferAndAllMeds = {
          ...values,
          ...onboardingForm,
          pharmacyDetails: onboardingForm.pharmacyStreets === '' ? null : onboardingForm.pharmacyStreets,
          pharmacyPhone: onboardingForm.pharmacyPhone === '' ? null : onboardingForm.pharmacyPhone,
          shouldTransferAllMeds: onboardingForm.shouldTransferAllMeds,
          phone: formatPhoneNumber(phone),
          medications: onboardingForm.shouldTransferAllMeds
            ? ['All medications']
            : isNoRxTransferFlow
            ? []
            : normalizedMeds,
          accountId,
          signupSource,
          referral,
        };

        try {
          await dispatchAccountOnboardingValidate({
            email,
            password,
            phone: data.phone,
            signupSource: data.signupSource,
          });
        } catch (error) {
          // The following code implements notifying users of an error if such an error is not displayed
          // beneath a form input.
          const snakeToCamelCase = {
            first_name: 'firstName',
            last_name: 'lastName',
          };

          const inputErrors = error.response?.data?.formErrors;
          const transformedErrors = {};
          let hasNonInputErrors = false;

          const filteredInputErrors = Object.keys(inputErrors).filter(key => key !== 'email');
          const emailFieldError = Object.keys(inputErrors).filter(key => key === 'email');
          filteredInputErrors.forEach(key => {
            if (!inputNames.includes(key)) {
              hasNonInputErrors = false;
            }

            if (snakeToCamelCase[key]) {
              transformedErrors[snakeToCamelCase[key]] = inputErrors[key];
            } else {
              transformedErrors[key] = inputErrors[key];
            }
          });

          if (emailFieldError) {
            setFormError(inputErrors.email);
            return;
          }

          if (filteredInputErrors) {
            setErrors(inputErrors);
          }

          if (hasNonInputErrors) {
            setFormError('Something went wrong. Please try submitting again.');
            Sentry.captureException(error);
          }
        }

        try {
          const { client } = state;
          const flow = getOnboardingFlow(state);
          const shouldUseMatchEndpoint = !isTransfer || flow !== FLOW.TRANSFER;

          const response = await accountSingleStepTransferCreate(client, data, userValues, shouldUseMatchEndpoint);
          const { accountId, userId, match_type: matchType } = response?.data ?? {};

          const actualTransfers = transformOnboardingTransfers(normalizedMeds, !!data.doctorName);

          dispatch(setMedicationSuccess(actualTransfers));
          dispatch(setOnboardingTransfersSuccess(actualTransfers));

          await login(email, password);

          dispatch(segmentAnalyticsIdentify(accountId)); // associate the accountId with this session
          dispatch(segmentTrack(UNIFIED.createAccount, {}));
          // Onboarding Personalization logic
          if (shouldUseMatchEndpoint) {
            if (response.status === 200) {
              const timestamp = Date.now();
              const onboardingInfo = JSON.parse(localStorage.getItem('personalizedOnboarding'));
              localStorage.setItem(
                'personalizedOnboarding',
                JSON.stringify({
                  ...onboardingInfo,
                  timestamp,
                  intent: isTransfer ? flow : FLOW.NO_RX,
                  matchType: isTransfer ? matchType : undefined,
                })
              );
            }
            // If this user matches an existing account check for escripts / meds
            // TODO this could be moved to the BFF onboarding match endpoint
            if (matchType === MATCH_TYPE.FULL_ONLY_ONE_MATCH) {
              try {
                const escriptCounts = await client.getIncompleteEscriptCount(accountId);
                const patientMeds = await getPatientMedications(client, accountId);
                dispatch(setOnboardingEscripts(escriptCounts?.data));
                dispatch(setMedicationSuccess(patientMeds));
              } catch (err) {
                // TODO: Handle error
              }
            }
            // Create account endpoint for transfers already sets TOS accepted correctly, but does not
            // return an userId. So we only need to accept TOs on the non-transfer flow.
            acceptTos(client, { accountId, userId });
          }

          dispatch(onboardingAccountNotifyTransfer(accountId));

          clearOnboardingStorage();
          if (isTransfer) {
            dispatchSegmentTrack(UNIFIED.transferSuccessful, {
              medication_names,
              page: pathname,
              type: 'Manual',
              account_holder: 'Primary',
            });
          }
          redirectToConfirmationPage();
        } catch (error) {
          Sentry.captureException(error);
          dispatchSegmentTrack(UNIFIED.transferFailed, {
            error_code: error?.response?.status,
            error_summary: error?.response?.statusText,
            page: pathname,
          });
        }
      }}
    >
      {({ values, isSubmitting, dirty, isValid }) => (
        <>
          {isSubmitting ? (
            <StyledSpinnerContainer>
              <Spinner centered={false} />
            </StyledSpinnerContainer>
          ) : (
            <Step
              title={'Tell us more about you'}
              text={`We need this information in order to complete your account`}
              customHandlePrevText={'Back'}
            >
              <Form autoComplete="off">
                <div ref={formErrorRef}>
                  {formError !== '' && (
                    <GenericError
                      errors={{
                        _generic: formError,
                      }}
                    />
                  )}
                </div>
                <TextInput
                  name="firstName"
                  type="text"
                  label="Legal first name"
                  onBlur={() => {
                    onSetOnboardingFormValues(values);
                  }}
                />
                <TextInput
                  name="lastName"
                  type="text"
                  label="Last name"
                  onBlur={() => {
                    onSetOnboardingFormValues(values);
                  }}
                />
                <DateTextInput
                  name="birthdate"
                  label="Date of birth"
                  onBlur={() => {
                    onSetOnboardingFormValues(values);
                  }}
                />
                <ToggleInput
                  name="sex"
                  label="BIRTH SEX"
                  options={toggleValues}
                  trackClick={handleGenderSelect}
                  isOnboardingFlow={true}
                />
                <TextInput
                  autoComplete="none"
                  name="phone"
                  type="tel"
                  label="Mobile number"
                  alwaysShowMask={false}
                  maskChar={null}
                  mask={phoneNumberMask}
                  onBlur={() => {
                    onSetOnboardingFormValues(values);
                  }}
                />
                <PasswordInput
                  autoComplete="new-password"
                  label="Set password"
                  onBlur={() => {
                    onSetOnboardingFormValues(values);
                  }}
                />

                <ButtonContainer smallMarginTop>
                  <Button
                    className={classNames(styles.fixedWidth, styles.noMargins, styles.borderRadius)}
                    size="medium"
                    type="submit"
                    style={'capsuleBlue50'}
                    disabled={!(dirty && isValid)}
                  >
                    Submit
                  </Button>
                </ButtonContainer>

                <LegalMessage>
                  By clicking submit, I agree to receive account and delivery text messages. View{' '}
                  <LinkWithQuery to={termsAndConditions.route} target="_blank">
                    Terms
                  </LinkWithQuery>
                  . Msg frequency varies, msg and data rates may apply. Reply STOP to cancel.
                </LegalMessage>
              </Form>
            </Step>
          )}
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    onboardingForm: state.onboardingForm,
    flow: state.onboardingForm.flow,
    pathname: getCurrentPathname(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onSetOnboardingFormValues: step => dispatch(setOnboardingFormValues(step)),
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
  dispatchAccountOnboardingValidate: data => dispatch(onboardingAccountValidate(data)),
  redirectToConfirmationPage: () => dispatch(pushWithQueryParams(welcome.toPath())),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

const mapFlowToSignupSource = (flow: string, isNoRxTransferFlow: boolean, medications: OnboardingTransfer[]) => {
  if (flow === FLOW.NO_RX || isNoRxTransferFlow || medications.length === 0) {
    return SIGNUP_SOURCES.WEB_ACCOUNT_CREATION;
  }

  if (flow === FLOW.DOCTOR_ESCRIPT) {
    return SIGNUP_SOURCES.WEB_ESCRIPT_FLOW;
  }

  return SIGNUP_SOURCES.WEB_TRANSFER_FLOW;
};

export const ProfileStep = compose(connector)(ProfileStepRenderer);
